import React, { useEffect, useRef } from 'react';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Image, { IImage } from 'tbk-components/src/components/Image';

export interface ILocationListFrequency {
	label?: string;
	value?: string;
	id?: string;
}

export interface ILocationListTimeframe {
	label?: string;
	value?: string;
	id?: string;
}

export interface ILocationListAmount {
	label?: string;
	value?: string;
	id?: string;
}

export interface ILocationListCustom {
	value?: string;
}

export interface ILocationListSubmit {
	value?: string;
}

export interface ILocationListForm {
	frequencies?: ILocationListFrequency[];
	timeframes?: ILocationListTimeframe[];
	amounts?: ILocationListAmount[];
	custom?: ILocationListCustom;
	submit?: ILocationListSubmit;
}

export interface ILocationListResults {
	image?: IImage;
	code?: string;
	heading?: string;
	subheading?: string;
	description?: string;
	form?: ILocationListForm;
}

export interface ILocationListResultsProps extends IBaseElementProps {
	results: ILocationListResults[];
}

const LocationListResults: React.FC<ILocationListResultsProps> = ({
	results,
}) => {
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!ref.current) {
			return;
		}

		const expandTextbox = (event: any) => {
			const span = event.target?.previousSibling;
			const text = event.target?.value;
			span.textContent = text;
		};

		const expandTimeFrame = (event: any) => {
			if (event.target?.value === 'recurring') {
				const fieldset = event.target?.parentNode?.parentNode
					?.parentNode?.nextSibling as HTMLDivElement;
				fieldset.classList.add('active');
			} else {
				const fieldset = event.target?.parentNode?.parentNode
					?.parentNode?.nextSibling as HTMLDivElement;
				fieldset.classList.remove('active');
			}
		};

		(
			ref.current.querySelectorAll(
				'.location-list__textbox input',
			) as NodeListOf<HTMLInputElement>
		).forEach((item) => {
			item.addEventListener('keyup', expandTextbox);
		});

		(
			ref.current.querySelectorAll(
				'.location-list__radio--frequency input',
			) as NodeListOf<HTMLInputElement>
		).forEach((item) => {
			item.addEventListener('click', expandTimeFrame);
		});

		return () => {
			(
				ref.current.querySelectorAll(
					'.location-list__textbox input',
				) as NodeListOf<HTMLInputElement>
			).forEach((item) => {
				item.removeEventListener('keyup', expandTextbox);
			});

			(
				ref.current.querySelectorAll(
					'.location-list__radio--frequency input',
				) as NodeListOf<HTMLInputElement>
			).forEach((item) => {
				item.removeEventListener('click', expandTimeFrame);
			});
		};
	}, []);

	return (
		<div className="location-list__results" ref={ref}>
			{results && (
				<ul className="location-list__results-items">
					{results.map((result, itemIndex) => (
						<li
							key={itemIndex}
							className="location-list__results-item"
						>
							{result.image && (
								<div className="location-list__results-image">
									{result.image && (
										<Image {...result.image} />
									)}
								</div>
							)}
							<div className="location-list__results-content">
								<div className="location-list__results-text">
									{result.code && (
										<div className="location-list__results-code">
											{result.code}
										</div>
									)}
									{result.heading && (
										<h3 className="location-list__results-heading">
											{result.heading}
										</h3>
									)}
									{result.subheading && (
										<div className="location-list__results-subheading">
											{' '}
											{result.subheading}
										</div>
									)}
									{result.description && (
										<p className="location-list__results-description">
											{result.description}
										</p>
									)}
								</div>
								<div className="location-list__results-form">
									{result?.form?.frequencies && (
										<fieldset className="location-list__results-fieldset">
											<legend className="visually-hidden">
												Frequency Select
											</legend>
											{result?.form?.frequencies.map(
												(radio, index) => (
													<div
														key={index}
														className="location-list__radio location-list__radio--frequency"
													>
														<label
															htmlFor={
																'frequency-' +
																itemIndex +
																'-' +
																index
															}
														>
															{index === 0 ? (
																<input
																	type="radio"
																	value={
																		radio?.value
																	}
																	id={
																		'frequency-' +
																		itemIndex +
																		'-' +
																		index
																	}
																	name={
																		'frequency-' +
																		itemIndex
																	}
																	defaultChecked
																/>
															) : (
																<input
																	type="radio"
																	value={
																		radio?.value
																	}
																	id={
																		'frequency-' +
																		itemIndex +
																		'-' +
																		index
																	}
																	name={
																		'frequency-' +
																		itemIndex
																	}
																/>
															)}
															<span>
																{radio?.label}
															</span>
														</label>
													</div>
												),
											)}
										</fieldset>
									)}

									{result?.form?.timeframes && (
										<fieldset className="location-list__results-fieldset location-list__results-fieldset--timeframe">
											<legend className="visually-hidden">
												Frequency Select
											</legend>
											{result?.form?.timeframes.map(
												(radio, index) => (
													<div
														key={index}
														className="location-list__radio location-list__radio--timeframe"
													>
														<label
															htmlFor={
																'timeframe-' +
																itemIndex +
																'-' +
																index
															}
														>
															{index === 0 ? (
																<input
																	type="radio"
																	value={
																		radio?.value
																	}
																	id={
																		'timeframe-' +
																		itemIndex +
																		'-' +
																		index
																	}
																	name={
																		'timeframe-' +
																		itemIndex
																	}
																	defaultChecked
																/>
															) : (
																<input
																	type="radio"
																	value={
																		radio?.value
																	}
																	id={
																		'timeframe-' +
																		itemIndex +
																		'-' +
																		index
																	}
																	name={
																		'timeframe-' +
																		itemIndex
																	}
																/>
															)}
															<span>
																{radio?.label}
															</span>
														</label>
													</div>
												),
											)}
										</fieldset>
									)}

									{result?.form?.amounts && (
										<fieldset className="location-list__results-fieldset">
											<legend className="visually-hidden">
												Amounts
											</legend>
											{result?.form?.amounts.map(
												(radio, index) => (
													<div
														key={index}
														className="location-list__radio"
													>
														<label
															htmlFor={
																'amount-' +
																itemIndex +
																'-' +
																index
															}
														>
															{index === 0 ? (
																<input
																	type="radio"
																	value={
																		radio?.value
																	}
																	id={
																		'amount-' +
																		itemIndex +
																		'-' +
																		index
																	}
																	name={
																		'amount-' +
																		itemIndex
																	}
																	defaultChecked
																/>
															) : (
																<input
																	type="radio"
																	value={
																		radio?.value
																	}
																	id={
																		'amount-' +
																		itemIndex +
																		'-' +
																		index
																	}
																	name={
																		'amount-' +
																		itemIndex
																	}
																/>
															)}
															<span>
																{radio?.label}
															</span>
														</label>
													</div>
												),
											)}
										</fieldset>
									)}
									<div className="location-list__results-controls">
										{result?.form?.custom && (
											<div className="location-list__textbox">
												<label>
													<span>0</span>
													<input
														type="number"
														id={
															'custom-' +
															itemIndex
														}
														name={
															'custom-' +
															itemIndex
														}
														placeholder={
															result?.form?.custom
																?.value
														}
													/>
												</label>
												<div>CAD</div>
											</div>
										)}
										<input
											type="submit"
											className="location-list__submit"
											id={'submit-' + itemIndex}
											name={'submit-' + itemIndex}
											value={result?.form?.submit?.value}
										/>
									</div>
								</div>
							</div>
						</li>
					))}
				</ul>
			)}
		</div>
	);
};

export default LocationListResults;
